import React, { useMemo } from 'react';
import useSWR from 'swr';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { fetcher } from '../../services/fetcher';
import { buildListQuery, normalizePaginatedListResponse, type PaginatedApiResponse } from '../../services/paginatedList';
import type { SupplyRequisition } from '../../modules/supplies/requisitions/useData';
import type { SupplyCase } from '../../modules/supplies/supply-cases/useData';
import type { VesselPendingPurchaseOrderRow } from '../pending-deliveries/useData';

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

type PendingForVesselResponse = PaginatedApiResponse<VesselPendingPurchaseOrderRow> | VesselPendingPurchaseOrderRow[];
type RequisitionsResponse = PaginatedApiResponse<SupplyRequisition> | SupplyRequisition[];
type CasesResponse = PaginatedApiResponse<SupplyCase> | SupplyCase[];

type PipelineStatusMeta = {
  label: string;
  toneClass: string;
  dotClass: string;
  stepLabel: string;
};

function getRequisitionPipelineStatus(row: SupplyRequisition): PipelineStatusMeta {
  const caseId = row.caseId ?? 0;
  const enquiryCount = row.pipelineCounts?.enquiryCount ?? 0;
  const quotationCount = row.pipelineCounts?.quotationCount ?? 0;
  const orderCount = row.pipelineCounts?.orderCount ?? 0;
  const deliveryCount = row.pipelineCounts?.deliveryCount ?? 0;

  if (caseId <= 0) {
    return {
      label: 'Awaiting office to send for RFQ',
      toneClass: 'border-slate-300/80 bg-slate-100/80 text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200',
      dotClass: 'bg-slate-500 dark:bg-slate-400',
      stepLabel: 'Step 1',
    };
  }
  if (orderCount > 0 && deliveryCount === 0) {
    return {
      label: 'Pending Delivery to Vessel',
      toneClass: 'border-emerald-300/80 bg-emerald-100/80 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
      dotClass: 'bg-emerald-500 dark:bg-emerald-400',
      stepLabel: 'Step 5',
    };
  }
  if (orderCount > 0 && deliveryCount > 0) {
    return {
      label: 'Delivered to Vessel',
      toneClass: 'border-teal-300/80 bg-teal-100/80 text-teal-900 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-100',
      dotClass: 'bg-teal-500 dark:bg-teal-400',
      stepLabel: 'Step 6',
    };
  }
  if (quotationCount > 0 && orderCount === 0) {
    return {
      label: 'Awaiting Office to proceed with Order',
      toneClass: 'border-blue-300/80 bg-blue-100/80 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-200',
      dotClass: 'bg-blue-500 dark:bg-blue-400',
      stepLabel: 'Step 4',
    };
  }
  if (enquiryCount > 0 && quotationCount === 0 && orderCount === 0) {
    return {
      label: 'Awaiting Supplier to send Quote',
      toneClass: 'border-violet-300/80 bg-violet-100/80 text-violet-800 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200',
      dotClass: 'bg-violet-500 dark:bg-violet-400',
      stepLabel: 'Step 3',
    };
  }
  return {
    label: 'Awaiting Office to send RFQ',
    toneClass: 'border-amber-300/80 bg-amber-100/80 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
    dotClass: 'bg-amber-500 dark:bg-amber-400',
    stepLabel: 'Step 2',
  };
}

const CARD_THEME = [
  'from-indigo-500/15 to-indigo-500/5 border-indigo-400/40',
  'from-cyan-500/15 to-cyan-500/5 border-cyan-400/40',
  'from-violet-500/15 to-violet-500/5 border-violet-400/40',
  'from-emerald-500/15 to-emerald-500/5 border-emerald-400/40',
] as const;

function CasesIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M6 7V5h12v2m-1 0v12H7V7h10z" />
    </svg>
  );
}

function RequisitionIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7h8M8 11h8M8 15h5M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" />
    </svg>
  );
}

function DeliveryIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 17H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-1 9h2a2 2 0 002-2v-4h-5m0 0V9m0 2h5" />
    </svg>
  );
}

function QuantityIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 7h16M4 12h16M4 17h10" />
    </svg>
  );
}

function StatCard(props: { title: string; value: number; helper?: string; themeIndex: number; icon: React.ReactNode }) {
  const { title, value, helper, themeIndex, icon } = props;
  return (
    <article
      className={`rounded-2xl border bg-gradient-to-br p-5 shadow-sm backdrop-blur ${CARD_THEME[themeIndex % CARD_THEME.length]}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-300">{title}</p>
        <span className="inline-flex rounded-lg bg-white/60 p-1.5 text-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
          {icon}
        </span>
      </div>
      <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      {helper ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</p> : null}
    </article>
  );
}

function DataPanel(props: { title: string; subtitle: string; icon: React.ReactNode; children: React.ReactNode }) {
  const { title, subtitle, icon, children } = props;
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/70">
      <div className="mb-4 flex items-center gap-2 border-b border-slate-200 pb-3 dark:border-slate-700">
        <span className="inline-flex rounded-lg bg-slate-100 p-1.5 text-slate-600 dark:bg-slate-800 dark:text-slate-200">{icon}</span>
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

const List: React.FC = () => {
  const { user } = useAuth();
  const vesselId = useAccountVesselScope();

  const requisitionsQuery = useMemo(() => {
    if (vesselId == null) return null;
    return buildListQuery(undefined, 1, 5, {
      sortKey: 'createdAt',
      sortDirection: 'desc',
      filters: { filters: { vesselId: [String(vesselId)] } },
    });
  }, [vesselId]);

  const casesQuery = useMemo(() => {
    if (vesselId == null) return null;
    return buildListQuery(undefined, 1, 5, {
      sortKey: 'createdAt',
      sortDirection: 'desc',
      filters: { filters: { vesselId: [String(vesselId)] } },
    });
  }, [vesselId]);

  const pendingQuery = useMemo(() => {
    if (vesselId == null) return null;
    const params = new URLSearchParams();
    params.set('vesselId', String(vesselId));
    params.set('page', '1');
    params.set('limit', '200');
    return `?${params.toString()}`;
  }, [vesselId]);

  const requisitions = useSWR<RequisitionsResponse>(
    requisitionsQuery ? `/supply-requisitions${requisitionsQuery}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  const supplyCases = useSWR<CasesResponse>(
    casesQuery ? `/supply-cases${casesQuery}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  const pending = useSWR<PendingForVesselResponse>(
    pendingQuery ? `/supply-purchase-orders/pending-for-vessel${pendingQuery}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );

  if (vesselId == null) {
    return (
      <div className="p-6">
        <h1 className="mb-2 text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-red-600 dark:text-red-400">
          This dashboard requires a vessel-scoped login. No vessel is linked to this account.
        </p>
      </div>
    );
  }

  const requisitionsList = normalizePaginatedListResponse<SupplyRequisition>(requisitions.data, 1, 5);
  const casesList = normalizePaginatedListResponse<SupplyCase>(supplyCases.data, 1, 5);
  const pendingList = normalizePaginatedListResponse<VesselPendingPurchaseOrderRow>(pending.data, 1, 200);
  const totalPendingQty = pendingList.data.reduce((sum, row) => sum + Number(row.totalPendingQty || 0), 0);
  const loading = requisitions.isLoading || supplyCases.isLoading || pending.isLoading;

  return (
    <div className="p-6 space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:to-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Vessel Operations Dashboard</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {user?.vessel?.vesselName ? `${user.vessel.vesselName} · ` : ''}Live operational snapshot for vessel ID {vesselId}
            </p>
          </div>
          <span className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 dark:border-slate-600 dark:text-slate-200">
            Vessel Scope
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Open Cases" value={casesList.total} helper="Current scoped supply cases" themeIndex={0} icon={<CasesIcon />} />
        <StatCard title="Requisitions" value={requisitionsList.total} helper="Submitted by this vessel" themeIndex={1} icon={<RequisitionIcon />} />
        <StatCard title="Pending Deliveries" value={pendingList.total} helper="Orders awaiting receipt" themeIndex={2} icon={<DeliveryIcon />} />
        <StatCard title="Total Pending Qty" value={totalPendingQty} helper="Aggregate undelivered units" themeIndex={3} icon={<QuantityIcon />} />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          Loading vessel metrics...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DataPanel title="Recent Requisitions" subtitle="Latest requisition activity" icon={<RequisitionIcon />}>
            <div className="space-y-2">
              {requisitionsList.data.length === 0 ? (
                <p className="rounded-xl border border-dashed p-4 text-sm text-slate-500 dark:text-slate-400">No requisitions found.</p>
              ) : (
                requisitionsList.data.map((row) => (
                  <div key={row.id} className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
                    <p className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                      <span className="inline-flex text-slate-500"><RequisitionIcon /></span>
                      {row.ref || `REQ #${row.id}`}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(row.reqDate || row.createdAt)}</p>
                    {(() => {
                      const status = getRequisitionPipelineStatus(row);
                      return (
                        <div className="mt-2 max-w-full" title={status.label}>
                          <div
                            className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tracking-tight ${status.toneClass}`}
                          >
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/15 ${status.dotClass}`} aria-hidden />
                            <span className="shrink-0 text-[0.68rem] uppercase opacity-85">{status.stepLabel}</span>
                            <span className="min-w-0 truncate">Pipeline Status: {status.label}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))
              )}
            </div>
          </DataPanel>

          <DataPanel title="Recent Supply Cases" subtitle="Most recent vessel cases" icon={<CasesIcon />}>
            <div className="space-y-2">
              {casesList.data.length === 0 ? (
                <p className="rounded-xl border border-dashed p-4 text-sm text-slate-500 dark:text-slate-400">No cases found.</p>
              ) : (
                casesList.data.map((row) => (
                  <div key={row.id} className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
                    <p className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                      <span className="inline-flex text-slate-500"><CasesIcon /></span>
                      {row.ref || `CASE #${row.id}`}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">ETA: {formatDate(row.eta)}</p>
                  </div>
                ))
              )}
            </div>
          </DataPanel>

          <DataPanel title="Pending Deliveries" subtitle="Top orders pending receipt" icon={<DeliveryIcon />}>
            <div className="space-y-2">
              {pendingList.data.slice(0, 5).length === 0 ? (
                <p className="rounded-xl border border-dashed p-4 text-sm text-slate-500 dark:text-slate-400">No pending deliveries.</p>
              ) : (
                pendingList.data.slice(0, 5).map((row) => (
                  <div key={row.id} className="rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
                    <p className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                      <span className="inline-flex text-slate-500"><DeliveryIcon /></span>
                      {row.ref || `PO #${row.id}`}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Pending Lines: {row.pendingLineCount} · Pending Qty: {row.totalPendingQty}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DataPanel>
        </div>
      )}
    </div>
  );
};

export default List;
